/**
 * Quick-Buy Confirmation Modal
 *
 * Listens for cart:update events that carry suppressDrawer + addedItem and
 * shows a centered confirmation card. Uses display:none → display:grid for
 * the overlay (guaranteed instant paint) plus a purely decorative card
 * slide-in on the next animation frame.
 *
 * Event source:
 *   - product-form-component (quick-add overlay button, form submit path)
 *   - quick-add-component    (fast-checkout button, handleQuickBuy path)
 * Both dispatch CartAddEvent which bubbles to document with { bubbles: true }.
 */

const CART_ADD_EVENT = 'cart:update';

class QuickBuyConfirmation extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #autoCloseTimer = null;

  /** @type {AbortController | null} */
  #listenerAbort = null;

  connectedCallback() {
    // Bubble-phase listener on document — identical approach to cart-drawer.js
    // which works reliably in all environments including the Shopify theme editor.
    document.addEventListener(CART_ADD_EVENT, this.#onCartAdd);
  }

  disconnectedCallback() {
    document.removeEventListener(CART_ADD_EVENT, this.#onCartAdd);
    this.#clearTimers();
  }

  // ─── Event handler ──────────────────────────────────────────────────────────

  /** @param {Event} event */
  #onCartAdd = (event) => {
    const data = /** @type {{ addedItem?: { title?: string, image?: string, price?: number, url?: string }, suppressDrawer?: boolean } | undefined} */ (
      /** @type {any} */ (event)?.detail?.data
    );

    // Only show when this event was triggered from a quick-add with drawer suppressed
    if (!data?.suppressDrawer || !data?.addedItem) return;

    this.#show(data.addedItem);
  };

  // ─── Show / hide ─────────────────────────────────────────────────────────────

  /**
   * @param {{ title?: string, image?: string, price?: number, url?: string }} item
   */
  #show(item) {
    // Tear down any existing modal first
    this.querySelector('.qbc')?.remove();
    this.#clearTimers();

    const priceStr = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((item.price || 0) / 100);

    const root = this.#build(item, priceStr);

    // ── Step 1: append with display:none (default CSS state) ──────────────────
    this.appendChild(root);

    // ── Step 2: flip to display:grid immediately — this is a layout property
    //    change, not a paint/opacity change, so it's always instant regardless
    //    of browser focus, Shopify editor iframes, or paint throttling.
    root.classList.add('qbc--open');

    // ── Step 3: signal listeners (e.g. add-to-cart button animation) that the
    //    modal is now visible so they can hand off cleanly.
    document.dispatchEvent(new CustomEvent('qbc:shown', { detail: { item } }));

    // ── Step 4: on the NEXT frame the browser has painted the open state,
    //    so we can safely start the decorative card transition.
    requestAnimationFrame(() => root.classList.add('qbc--animating'));

    // ── Wire up close triggers ─────────────────────────────────────────────────
    const ac = new AbortController();
    this.#listenerAbort = ac;
    const { signal } = ac;

    const close = () => this.#close(root);

    root.querySelector('.qbc__backdrop')?.addEventListener('click', close, { signal });
    root.querySelector('.qbc__close')?.addEventListener('click', close, { signal });
    root.querySelector('.qbc__btn--secondary')?.addEventListener('click', close, { signal });

    // Auto-dismiss after 8 s
    this.#autoCloseTimer = setTimeout(close, 8000);
  }

  /** @param {HTMLElement} root */
  #close(root) {
    this.#clearTimers();

    root.classList.remove('qbc--animating');
    root.classList.add('qbc--closing');

    // Remove from DOM after the CSS transition finishes (280 ms)
    setTimeout(() => root.remove(), 300);
  }

  #clearTimers() {
    if (this.#autoCloseTimer !== null) {
      clearTimeout(this.#autoCloseTimer);
      this.#autoCloseTimer = null;
    }
    this.#listenerAbort?.abort();
    this.#listenerAbort = null;
  }

  // ─── DOM builder ─────────────────────────────────────────────────────────────

  /**
   * Builds the modal DOM tree without ever treating user data as HTML.
   * SVG icons are internal static strings — safe to use innerHTML only for them.
   *
   * @param {{ title?: string, image?: string, price?: number, url?: string }} item
   * @param {string} priceStr
   * @returns {HTMLElement}
   */
  #build(item, priceStr) {
    // ── Root overlay ──────────────────────────────────────────────────────────
    const root = /** @type {HTMLElement} */ (document.createElement('div'));
    root.className = 'qbc';

    // ── Backdrop ──────────────────────────────────────────────────────────────
    const backdrop = document.createElement('div');
    backdrop.className = 'qbc__backdrop';

    // ── Card ──────────────────────────────────────────────────────────────────
    const card = document.createElement('div');
    card.className = 'qbc__card';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', 'Added to your cart');

    // Close button (SVG × icon — static markup, not user data)
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'qbc__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

    // ── 1. Success banner ─────────────────────────────────────────────────────
    const banner = document.createElement('div');
    banner.className = 'qbc__banner';
    // Static SVG check icon — not user data
    banner.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="#22c55e"/><path d="M7 12.5l3.5 3.5 6-6" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const bannerText = document.createElement('span');
    bannerText.textContent = 'Added to your cart!';
    banner.appendChild(bannerText);

    // ── 2. Product preview ────────────────────────────────────────────────────
    const preview = document.createElement('div');
    preview.className = 'qbc__preview';

    if (item.image) {
      const img = document.createElement('img');
      img.className = 'qbc__thumb';
      const sep = item.image.includes('?') ? '&' : '?';
      img.src = `${item.image}${sep}width=200`;
      img.alt = item.title || '';
      img.width = 80;
      img.height = 80;
      img.loading = 'eager';
      img.decoding = 'async';
      preview.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'qbc__info';

    const titleEl = document.createElement('p');
    titleEl.className = 'qbc__title';
    titleEl.textContent = item.title || '';

    const priceEl = document.createElement('p');
    priceEl.className = 'qbc__price';
    priceEl.textContent = priceStr;

    info.appendChild(titleEl);
    info.appendChild(priceEl);
    preview.appendChild(info);

    // ── 3. Action buttons ─────────────────────────────────────────────────────
    const actions = document.createElement('div');
    actions.className = 'qbc__actions';

    const viewCartLink = document.createElement('a');
    viewCartLink.className = 'qbc__btn qbc__btn--secondary button button-secondary';
    viewCartLink.href = '/cart';
    viewCartLink.textContent = 'View cart';

    // Checkout goes through a real form POST so Shopify redirects properly
    const checkoutForm = document.createElement('form');
    checkoutForm.action = '/cart';
    checkoutForm.method = 'post';
    const checkoutBtn = document.createElement('button');
    checkoutBtn.type = 'submit';
    checkoutBtn.name = 'checkout';
    checkoutBtn.className = 'qbc__btn qbc__btn--primary button';
    checkoutBtn.textContent = 'Checkout';
    checkoutForm.appendChild(checkoutBtn);

    actions.appendChild(viewCartLink);
    actions.appendChild(checkoutForm);

    // ── Assemble ──────────────────────────────────────────────────────────────
    card.appendChild(closeBtn);
    card.appendChild(banner);
    card.appendChild(preview);
    card.appendChild(actions);
    root.appendChild(backdrop);
    root.appendChild(card);

    return root;
  }
}

if (!customElements.get('quick-buy-confirmation')) {
  customElements.define('quick-buy-confirmation', QuickBuyConfirmation);
}
